import jsPDF from 'jspdf';

export const downloadFileFromBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(new Blob([blob]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.parentNode.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const base64ToBlob = (b64Data, contentType = '') => {
  const byteCharacters = atob(b64Data);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512);
    const byteNumbers = new Array(slice.length);

    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  return new Blob(byteArrays, { type: contentType });
};

export const handleSampleDocumentsDownload = async (selectedDocs, downloadSampleDocuments) => {
  if (selectedDocs.length === 0) {
    throw new Error('Please select at least one document to download');
  }

  try {
    const response = await downloadSampleDocuments(selectedDocs).unwrap();

    if (response.data && response.data.fileContent) {
      const blob = base64ToBlob(response.data.fileContent, response.data.contentType || 'application/pdf');
      const filename = response.data.fileName || 'sample-documents.pdf';
      downloadFileFromBlob(blob, filename);
      return { success: true, message: response.message || 'Download started successfully' };
    }
  } catch (error) {
    console.error('Download failed:', error);
    if (error.status === 404) {
      throw new Error('No valid PDF documents found for downloading');
    }
    throw new Error(error.data?.message || error.message || 'Failed to download documents');
  }
};
export const downloadFile = async (fileUrl, fileName = 'document') => {
  try {
    // Ensure URL is safely encoded to handle spaces, parentheses, etc.
    const safeUrl = encodeURI(fileUrl);

    // Determine if URL is cross-origin (e.g., S3). If so, avoid fetch to bypass CORS issues.
    let urlObj;
    try {
      urlObj = new URL(safeUrl, window.location.href);
    } catch {
      urlObj = null;
    }
    const isCrossOrigin = urlObj ? urlObj.origin !== window.location.origin : true;
    const ext = urlObj ? urlObj.pathname.match(/\.([a-zA-Z0-9]+)$/)?.[1]?.toLowerCase() || '' : '';

    if (isCrossOrigin) {
      // Use local proxy to force attachment download and avoid CORS issues
      const suggestedName = `${fileName}${ext ? `.${ext}` : ''}`;
      const proxyUrl = `/api/proxy?url=${encodeURIComponent(safeUrl)}&filename=${encodeURIComponent(suggestedName)}`;
      const a = document.createElement('a');
      a.href = proxyUrl;
      a.setAttribute('download', suggestedName);
      document.body.appendChild(a);
      a.click();
      a.remove();
      return;
    }

    // Same-origin: use fetch to allow image->PDF conversion behavior
    const response = await fetch(safeUrl);
    if (!response.ok) throw new Error('Failed to download file');

    const blob = await response.blob();
    const fileType = blob?.type?.toLowerCase();

    if (fileType?.includes('pdf') || ext === 'pdf') {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileName}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      return;
    }

    if (fileType?.includes('image')) {
      const reader = new FileReader();
      reader.onload = () => {
        const imgData = reader.result;
        const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        pdf.addImage(imgData, 'JPEG', 0, 0, pageWidth, pageHeight);
        pdf.save(`${fileName}.pdf`);
      };
      reader.readAsDataURL(blob);
      return;
    }

    // Fallback: download as-is with detected/unknown extension
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fileName}${ext ? `.${ext}` : ''}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Download failed:', error);
    throw new Error('Failed to download file');
  }
};
